import type { CSSProperties } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Flex, Grid, theme } from 'antd';
import DateRangeFilter from './dateRangeFilter/DateRangeFilter';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import ShowsFilterEcho from './showsFilter/ShowsFilterEcho';
import NetworksFilterEcho from './networksFilter/NetworksFilterEcho';
import EpisodesFilter, { type EpisodeFilter } from './episodesFilter/EpisodesFilter';
import { getShowParam, mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MAX_NETWORK_IDS, MAX_SHOWS_PER_PAGE } from '@/utils/constants';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import PlatformFilter from './platformFilter/PlatformFilter';
import {
  getDateAppliedEventByPageType,
  getDatePickerOpenedEventByPageType,
  getDatePresetSelectedEventByPageType,
  getShowsFilterOpenedEventByPageType,
  getShowsFilterSelectedEventByPageType,
} from '@/utils/mixpanelUtils/pageTypeHelpers';
import { usePageMetadata, useFilterNavigation, useNetworkFilter } from '@/hooks';
import useSelectedShows from '@/hooks/useSelectedShows';
import useNetworks from '@/hooks/useNetworks';
import FiltersMultiSelectEcho from './FiltersMultiSelectEcho';
import type { LevelView } from '@/routes/ContentPerformancePage/DataLevelContainer';

export enum VisibleFilterKey {
  Networks = 'networks',
  Shows = 'shows',
  Episodes = 'episodes',
  Platform = 'platform',
  DateRange = 'dateRange',
}

type FiltersProps = {
  visibleFilters: VisibleFilterKey[];
  onFilterApplied?: () => void;
  /** When true, removes root padding so the block can sit flush in a narrow row (e.g. mobile filter bar). */
  stripOuterPadding?: boolean;
  onDataLevelFilterChange?: (level: LevelView) => void;
};

/**
 * This component is used to render the filters.
 * It contains the date range filter and other filters based on visibleFilters.
 * @param {VisibleFilterKey[]} visibleFilters - Which filters to show.
 * @returns {React.JSX.Element} - The filters.
 */
const FiltersEcho = ({
  visibleFilters,
  onFilterApplied,
  stripOuterPadding,
  onDataLevelFilterChange,
}: FiltersProps) => {
  const { page: pageType } = usePageMetadata();
  const { filters, setFilters } = useFilterContext();
  const { selectedNetwork, shows } = useDerivedNetworkContext();
  const { goToNetwork, goToShow, goToDateRange } = useFilterNavigation();
  const { data: networks } = useNetworks();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const isLargeScreenUp = Boolean(screens.lg);
  const noOfShows = shows.length;
  const prevNoOfShowsRef = useRef(noOfShows);

  // Local state for episode filter (mock data for now)
  const [selectedEpisode, setSelectedEpisode] = useState<EpisodeFilter>('all');

  if (!pageType) throw new Error('Page type is required');

  const filterColumnStyle: CSSProperties = {
    width: '100%',
    ...(isLargeScreenUp ? { flex: 1, minWidth: 0 } : {}),
  };

  const isAudiencePage = pageType === 'audience';
  const isContentPerformancePage = pageType === 'content-performance';

  const enableShowsMultiSelect = isContentPerformancePage;
  const MAX_SHOWS = MAX_SHOWS_PER_PAGE[pageType];

  const { selectedShowIds, setSelectedShowIds } = useSelectedShows(
    enableShowsMultiSelect,
    MAX_SHOWS
  );

  const { selectedNetworkIds, setSelectedNetworkIds, showNetworkMultiSelect } =
    useNetworkFilter(networks);

  const showNetworks = visibleFilters.includes(VisibleFilterKey.Networks);
  const showShows = visibleFilters.includes(VisibleFilterKey.Shows) && shows.length > 1;
  const showEpisodes = visibleFilters.includes(VisibleFilterKey.Episodes);
  const showPlatform = visibleFilters.includes(VisibleFilterKey.Platform);
  const showDateRange = visibleFilters.includes(VisibleFilterKey.DateRange);

  // Reset selected show to 'all' when shows become unavailable for selected date range, all is not available on audience page
  useEffect(() => {
    if (noOfShows === 0 && prevNoOfShowsRef.current > 0 && isAudiencePage) {
      setFilters((prev) => ({
        ...prev,
        selectedShows: [],
      }));
    }
    prevNoOfShowsRef.current = noOfShows;
  }, [noOfShows, setFilters, isAudiencePage]);

  return (
    <Flex
      vertical={!isLargeScreenUp}
      gap={token.marginMD}
      style={{
        width: '100%',
        padding: stripOuterPadding ? 0 : token.paddingLG,
        ...(isLargeScreenUp ? { flexGrow: 1 } : {}),
      }}
    >
      {showNetworks && (
        <Flex style={filterColumnStyle}>
          {showNetworkMultiSelect ? (
            <FiltersMultiSelectEcho
              labelSingular="network"
              items={networks ?? []}
              selectedIds={selectedNetworkIds}
              maxCount={MAX_NETWORK_IDS}
              onChange={(networkIds) => {
                setSelectedNetworkIds(networkIds);
                onDataLevelFilterChange?.(VisibleFilterKey.Networks);
                onFilterApplied?.();
              }}
            />
          ) : (
            <NetworksFilterEcho
              networks={networks ?? []}
              selectedNetwork={selectedNetwork}
              onOpen={() => {
                mixpanelTrack(MIXPANEL_EVENT.networkSelect.opened, {
                  widget: RevenueDashboardWidget.NetworkSelect,
                  page: pageType,
                });
              }}
              onApply={(networkId) => {
                const network = networks?.find((n) => n.id === networkId);
                const networkName = network?.name ?? networkId;
                mixpanelTrack(MIXPANEL_EVENT.networkSelect.selected, {
                  widget: RevenueDashboardWidget.NetworkSelect,
                  page: pageType,
                  networkName,
                });

                goToNetwork(networkId);
                onFilterApplied?.();
              }}
            />
          )}
        </Flex>
      )}

      {showShows && (
        <Flex style={filterColumnStyle}>
          {enableShowsMultiSelect ? (
            <FiltersMultiSelectEcho
              labelSingular="show"
              items={shows}
              allowClear
              selectedIds={selectedShowIds}
              maxCount={MAX_SHOWS}
              onChange={(showIds) => {
                setSelectedShowIds(showIds);
                onDataLevelFilterChange?.(VisibleFilterKey.Shows);
                mixpanelTrack(getShowsFilterSelectedEventByPageType(pageType), {
                  widget: RevenueDashboardWidget.ShowSelect,
                  page: pageType,
                  showId: showIds.join(','),
                  showName: showIds.join(','),
                });
                onFilterApplied?.();
              }}
              onOpen={() => {
                mixpanelTrack(getShowsFilterOpenedEventByPageType(pageType), {
                  widget: RevenueDashboardWidget.ShowSelect,
                  page: pageType,
                });
              }}
            />
          ) : (
            <ShowsFilterEcho
              renderAllShows={!isAudiencePage && !isContentPerformancePage}
              shows={shows}
              selectedShow={filters.selectedShows[0] ?? 'all'}
              onOpen={() => {
                mixpanelTrack(getShowsFilterOpenedEventByPageType(pageType), {
                  widget: RevenueDashboardWidget.ShowSelect,
                  page: pageType,
                });
              }}
              onApply={(newSelectedShow) => {
                mixpanelTrack(getShowsFilterSelectedEventByPageType(pageType), {
                  widget: RevenueDashboardWidget.ShowSelect,
                  page: pageType,
                  showId: getShowParam(newSelectedShow),
                  showName: getShowParam(newSelectedShow),
                });

                goToShow(getShowParam(newSelectedShow));
                onFilterApplied?.();
              }}
            />
          )}
        </Flex>
      )}

      {showEpisodes && (
        <Flex style={filterColumnStyle}>
          <EpisodesFilter
            selectedEpisode={selectedEpisode}
            onOpen={() => {
              mixpanelTrack(MIXPANEL_EVENT.earnings.episodesFilter.opened, {
                widget: RevenueDashboardWidget.EpisodeSelect,
                page: pageType,
              });
            }}
            onApply={(newSelectedEpisode) => {
              setSelectedEpisode(newSelectedEpisode);
              mixpanelTrack(MIXPANEL_EVENT.earnings.episodesFilter.selected, {
                widget: RevenueDashboardWidget.EpisodeSelect,
                page: pageType,
                episodeId: newSelectedEpisode === 'all' ? 'all' : newSelectedEpisode?.id,
              });
              onFilterApplied?.();
            }}
          />
        </Flex>
      )}

      {showPlatform && (
        <Flex style={filterColumnStyle}>
          <PlatformFilter
            onOpen={() => {
              mixpanelTrack(MIXPANEL_EVENT.earnings.platformFilter.opened, {
                widget: RevenueDashboardWidget.PlatformSelect,
                page: pageType,
              });
            }}
            onApply={(newSelectedPlatforms) => {
              setFilters({
                ...filters,
                selectedPlatforms: newSelectedPlatforms,
              });

              mixpanelTrack(MIXPANEL_EVENT.earnings.platformFilter.selected, {
                widget: RevenueDashboardWidget.PlatformSelect,
                page: pageType,
                platforms:
                  newSelectedPlatforms.length > 0
                    ? newSelectedPlatforms.map((p) => p.label).join(', ')
                    : 'all',
              });
              onFilterApplied?.();
            }}
          />
        </Flex>
      )}

      {showDateRange && (
        <Flex style={filterColumnStyle}>
          <DateRangeFilter
            dateRange={filters.dateRange}
            onOpen={() => {
              mixpanelTrack(getDatePickerOpenedEventByPageType(pageType), {
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
            }}
            onPresetSelect={(preset) => {
              mixpanelTrack(getDatePresetSelectedEventByPageType(pageType), {
                preset,
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
            }}
            onApply={(start, end) => {
              mixpanelTrack(getDateAppliedEventByPageType(pageType), {
                start,
                end,
                widget: RevenueDashboardWidget.DateSelect,
                page: pageType,
              });
              goToDateRange(start, end || start);
              onFilterApplied?.();
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};

export default FiltersEcho;
